import SecondaryButton from '@/components/Buttons/SecondaryButton';
import { Heading, Text } from '@/components/Typography';
import useRoutes from '@/hooks/useRoutes';
import { AssignmentSummary, EvaluationSummary, SectionData, TopicData } from '@/types';
import { Link } from '@inertiajs/react';
import clsx from 'clsx';
import { ArrowRight, CheckCircle, ChevronRight, ClipboardList, FileText, X } from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface SectionDrawerProps {
    section: SectionData | null;
    courseSlug: string;
    onClose: () => void;
}

const SectionDrawer: React.FC<SectionDrawerProps> = ({
    section,
    courseSlug,
    onClose,
}) => {
    const { t } = useTranslation();
    const { route } = useRoutes();

    const isOpen = section !== null;

    // Lock body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const buildTopicUrl = (topicId: number) =>
        route('topic-detail', {
            slug: courseSlug,
            topic_id: topicId,
        }).toString();

    const buildEvaluationUrl = (evaluationId: number) =>
        `/evaluations/${evaluationId}/`;

    const sectionUrl = section
        ? route('section-detail', {
              slug: courseSlug,
              section_id: section.id,
          }).toString()
        : '#';

    const totalTopics = section?.topics.length ?? 0;
    const totalClasses = section?.total_classes ?? 0;
    const completedClasses = section?.completed_classes ?? 0;
    const totalAssignments = section?.assignments.length ?? 0;
    const totalEvaluations = section?.evaluations.length ?? 0;

    // Group assignments and evaluations under their topic (they carry topic_id), so each topic
    // renders its own nested items — mirroring the full section page. Items without a topic stay
    // flat at the end.
    const { assignmentsByTopic, evaluationsByTopic, orphanAssignments, orphanEvaluations } = useMemo(() => {
        const aByTopic = new Map<number, AssignmentSummary[]>();
        const eByTopic = new Map<number, EvaluationSummary[]>();
        const orphanA: AssignmentSummary[] = [];
        const orphanE: EvaluationSummary[] = [];

        (section?.assignments ?? []).forEach((assignment) => {
            if (!assignment.topic_id) {
                orphanA.push(assignment);
                return;
            }
            const list = aByTopic.get(assignment.topic_id) ?? [];
            list.push(assignment);
            aByTopic.set(assignment.topic_id, list);
        });

        (section?.evaluations ?? []).forEach((evaluation) => {
            if (!evaluation.topic_id) {
                orphanE.push(evaluation);
                return;
            }
            const list = eByTopic.get(evaluation.topic_id) ?? [];
            list.push(evaluation);
            eByTopic.set(evaluation.topic_id, list);
        });

        return {
            assignmentsByTopic: aByTopic,
            evaluationsByTopic: eByTopic,
            orphanAssignments: orphanA,
            orphanEvaluations: orphanE,
        };
    }, [section]);

    const renderAssignmentRow = (assignment: AssignmentSummary) => (
        <Link
            key={`assignment-${assignment.id}`}
            href={`/assignments/${assignment.slug}/`}
            className="group flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 dark:bg-neutral-700">
                <ClipboardList size={14} className="text-emerald-500 transition-colors group-hover:text-theme-primary-text" />
            </div>
            <div className="min-w-0 flex-1">
                <Text as="p" size="sm" className="truncate font-medium text-gray-800 dark:text-neutral-200">
                    {assignment.title}
                </Text>
                <Text as="p" size="xs" muted className="mt-0.5 dark:text-neutral-400">
                    {t('course.assignment', 'tarea')}
                </Text>
            </div>
            <ChevronRight size={16} className="flex-shrink-0 text-gray-300 transition-colors group-hover:text-theme-primary-text" />
        </Link>
    );

    const renderEvaluationRow = (evaluation: EvaluationSummary) => (
        <Link
            key={`evaluation-${evaluation.id}`}
            href={buildEvaluationUrl(evaluation.id)}
            className="group flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:bg-neutral-800 dark:hover:bg-neutral-700"
        >
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 dark:bg-neutral-700">
                <FileText size={14} className="text-gray-500 transition-colors group-hover:text-theme-primary-text" />
            </div>
            <div className="min-w-0 flex-1">
                <Text as="p" size="sm" className="truncate font-medium text-gray-800 dark:text-neutral-200">
                    {evaluation.title}
                </Text>
                <Text as="p" size="xs" muted className="mt-0.5 dark:text-neutral-400">
                    1 {t('course.evaluation', 'evaluación')}
                </Text>
            </div>
            <ChevronRight size={16} className="flex-shrink-0 text-gray-300 transition-colors group-hover:text-theme-primary-text" />
        </Link>
    );

    const metaParts: string[] = [];
    if (totalTopics > 0) {
        metaParts.push(`${totalTopics} ${t('course.topics', 'temas')}`);
    }
    if (totalClasses > 0) {
        metaParts.push(`${completedClasses}/${totalClasses} ${t('course.classes', 'clases')}`);
    }
    if (totalAssignments > 0) {
        metaParts.push(`${totalAssignments} ${t('course.assignments', 'tareas')}`);
    }
    if (totalEvaluations > 0) {
        metaParts.push(`${totalEvaluations} ${t('course.evaluation', 'evaluación')}`);
    }

    return (
        <>
            {/* Backdrop */}
            <div
                className={clsx(
                    'fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300',
                    isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
                onClick={onClose}
            />

            {/* Drawer panel */}
            <div
                className={clsx(
                    'fixed inset-y-0 right-0 z-50 flex w-full flex-col overflow-hidden shadow-2xl transition-transform duration-300 ease-in-out sm:max-w-md',
                    'bg-stone-100 dark:bg-neutral-900',
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                {section && (
                    <>
                        {/* Header */}
                        <div className="flex-shrink-0 border-b border-stone-200 bg-stone-100 p-5 dark:border-neutral-700 dark:bg-neutral-900">
                            <div className="flex items-start gap-3">
                                {/* Section icon */}
                                {section.icon_url && (
                                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white p-2 shadow-sm">
                                        <img
                                            src={section.icon_url}
                                            alt={section.title}
                                            className="h-full w-full object-contain"
                                        />
                                    </div>
                                )}

                                {/* Title + meta */}
                                <div className="min-w-0 flex-1">
                                    <Link
                                        href={sectionUrl}
                                        className="inline-block transition-colors hover:text-theme-primary-text"
                                    >
                                        <Heading as="h2" className="text-base uppercase tracking-wide dark:text-white">
                                            {section.title}
                                        </Heading>
                                    </Link>
                                    {metaParts.length > 0 && (
                                        <p className="mt-0.5 text-sm text-gray-500 dark:text-neutral-400">
                                            {metaParts.join(' • ')}
                                        </p>
                                    )}
                                </div>

                                {/* Close button */}
                                <button
                                    onClick={onClose}
                                    className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-stone-200 hover:text-gray-600 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-200"
                                    aria-label="Cerrar"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Completed badge */}
                            {section.is_completed && (
                                <div className="mt-3">
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-200 px-3 py-1 text-xs font-medium text-gray-600">
                                        <CheckCircle size={13} />
                                        {t('course.completed', 'Completado')}
                                    </span>
                                </div>
                            )}

                            <div className="mt-4">
                                <SecondaryButton as="link" href={sectionUrl} size="sm">
                                    {t('course.viewFullSection', 'Ver sección completa')}
                                    <ArrowRight size={14} />
                                </SecondaryButton>
                            </div>
                        </div>

                        {/* Scrollable content */}
                        <div className="flex-1 overflow-y-auto p-5">
                            <div className="space-y-2">
                                {/* Topic rows, each with its nested assignments and evaluations */}
                                {section.topics.map((topic: TopicData, index: number) => {
                                    const topicAssignments = assignmentsByTopic.get(topic.id) ?? [];
                                    const topicEvaluations = evaluationsByTopic.get(topic.id) ?? [];
                                    const hasChildren = topicAssignments.length > 0 || topicEvaluations.length > 0;

                                    return (
                                        <div key={topic.id} className="space-y-2">
                                            <Link
                                                href={buildTopicUrl(topic.id)}
                                                className="group flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 dark:bg-neutral-800 dark:hover:bg-neutral-700"
                                            >
                                                {/* Number badge */}
                                                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-xs font-semibold text-gray-600 dark:bg-neutral-700 dark:text-neutral-300">
                                                    {index + 1}
                                                </div>

                                                {/* Topic info */}
                                                <div className="min-w-0 flex-1">
                                                    <Text as="p" size="sm" className="truncate font-medium text-gray-800 dark:text-neutral-200">
                                                        {topic.title}
                                                    </Text>
                                                    {topic.classes_count > 0 && (
                                                        <Text as="p" size="xs" muted className="mt-0.5 dark:text-neutral-400">
                                                            {topic.completed_classes ?? 0}/{topic.classes_count}{' '}
                                                            {t('course.classes', 'clases')}
                                                        </Text>
                                                    )}
                                                </div>

                                                <ChevronRight size={16} className="flex-shrink-0 text-gray-300 transition-colors group-hover:text-theme-primary-text" />
                                            </Link>

                                            {/* Nested assignments + evaluations for this topic */}
                                            {hasChildren && (
                                                <div className="ml-4 space-y-2 border-l-2 border-stone-200 pl-3 dark:border-neutral-700">
                                                    {topicAssignments.map(renderAssignmentRow)}
                                                    {topicEvaluations.map(renderEvaluationRow)}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Section-level items not attached to any topic (kept flat) */}
                                {orphanAssignments.map(renderAssignmentRow)}
                                {orphanEvaluations.map(renderEvaluationRow)}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default SectionDrawer;
